import { BellIcon } from '@heroicons/react/24/outline';
import { useId, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  NotificationCenter as NotificationCenterPrimitive,
  NotificationCenterBadge,
  NotificationCenterContent,
  NotificationCenterHeader,
  NotificationCenterTrigger,
} from '@/components/ui/notification-center';
import { useIntegrationSuggestions } from '@/hooks/useIntegrationSuggestions';
import { JobsActivity } from '@/sections/topNavBar/NotificationCenter/JobsActivity';
import { SuggestionsInbox } from '@/sections/topNavBar/NotificationCenter/SuggestionsInbox';
import { StorageKey, useStorage } from '@/utils/storage/useStorage.util';

/**
 * The Activity Hub (ENG-5155): a bell trigger + popover in the top nav that
 * composes the shadcn notification-center primitive with the Activity section
 * (job counts + running-class breakdown) and the Suggestions inbox
 * (undismissed integration suggestions).
 *
 * The badge counts undismissed suggestions on the info accent — activity is
 * ambient status, never an alarm, so it must never render red. A one-time
 * intro toast fires on first open, latched forever via localStorage
 * (`chariot.activityHubIntroSeen`).
 */
export function NotificationCenter() {
  const [open, setOpen] = useState(false);
  // Names the popover's role="dialog" via aria-labelledby (axe
  // aria-dialog-name, WCAG 4.1.2) — points at the visible header title.
  const headerId = useId();
  const { suggestions } = useIntegrationSuggestions();
  const [introSeen, setIntroSeen] = useStorage<boolean>(
    { key: StorageKey.ACTIVITY_HUB_INTRO_SEEN },
    false
  );

  const undismissedCount = suggestions.length;
  const ariaLabel =
    undismissedCount > 0
      ? `Activity Hub — ${undismissedCount} new suggestion${
          undismissedCount === 1 ? '' : 's'
        }`
      : 'Activity Hub';

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen);
    if (nextOpen && !introSeen) {
      setIntroSeen(true);
      toast.info(
        'Job activity and integration suggestions now live here in the Activity Hub.'
      );
    }
  }

  return (
    <NotificationCenterPrimitive open={open} onOpenChange={handleOpenChange}>
      <NotificationCenterTrigger asChild>
        <Button variant="ghost" className="size-11" aria-label={ariaLabel}>
          <BellIcon className="size-5" aria-hidden="true" />
          <NotificationCenterBadge count={undismissedCount} />
        </Button>
      </NotificationCenterTrigger>
      {/* max-w keeps the locked 380px panel on-screen at narrow viewports
          (≤375px) — tailwind-merge lets it coexist with the primitive's
          locked `w-[380px]`. */}
      <NotificationCenterContent
        aria-labelledby={headerId}
        className="max-w-[calc(100vw-16px)]"
      >
        <NotificationCenterHeader>
          <span
            id={headerId}
            className="text-chariot-text-primary text-sm font-semibold"
          >
            Activity Hub
          </span>
        </NotificationCenterHeader>
        <JobsActivity open={open} onClose={() => setOpen(false)} />
        <SuggestionsInbox onClose={() => setOpen(false)} />
      </NotificationCenterContent>
    </NotificationCenterPrimitive>
  );
}
